import { ApiError, type ProblemDetails } from './api-error'
import type { AccessTokenOptions } from './auth-context'

export interface ApiRequestOptions {
  /** Query-Parameter. `undefined` und `null` werden weggelassen. */
  query?: Record<string, string | number | boolean | null | undefined>
  /** Zusätzliche Header. Authorization setzt der Client selbst. */
  headers?: Record<string, string>
  /** Zum Abbrechen, z.B. aus TanStack Query. */
  signal?: AbortSignal
}

export interface ApiClient {
  get<T>(path: string, options?: ApiRequestOptions): Promise<T>
  /** Lädt Binärdaten mit derselben Anmeldung und Fehlerbehandlung wie JSON-Aufrufe. */
  download(path: string, options?: ApiRequestOptions): Promise<Blob>
  /**
   * Lädt eine Datei roh hoch — der Rumpf ist die Datei selbst, ohne
   * JSON-Verpackung und ohne Formular.
   *
   * `url` darf absolut sein. Genau das ist der Regelfall: Die Adresse kommt aus
   * `IDocumentStore.CreateUploadAsync` und zeigt auf die **Plattform**, nicht
   * auf den Modulpfad — der Inhalt fließt durch den Host, nie am ihm vorbei.
   *
   * Token, `X-Trace-Id`, die einmalige Wiederholung nach 401 und die
   * Fehlerübersetzung gelten wie bei jedem anderen Aufruf.
   */
  upload<T>(url: string, file: Blob, options?: ApiRequestOptions): Promise<T>
  /**
   * Holt eine Datei von einer absoluten Adresse als `Blob`, mit Anmeldung.
   *
   * Für Adressen aus `IDocumentStore.GetDownloadUrlAsync`. Die sind
   * personengebunden und verlangen einen `Authorization`-Kopf — deshalb
   * funktioniert dort weder ein `<a href>` noch `window.open`, und deshalb gibt
   * es diese Methode. Für Pfade des eigenen Moduls bleibt {@link download}.
   */
  downloadFrom(url: string, options?: ApiRequestOptions): Promise<Blob>
  post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T>
  put<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T>
  patch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T>
  delete<T>(path: string, options?: ApiRequestOptions): Promise<T>
}

export interface ApiClientOptions {
  /** Basis-Pfad des Moduls, z.B. `/api/wartung`. */
  baseUrl: string
  /** Scopes, die dieses Modul für seine API braucht. */
  scopes: string[]
  /** Holt ein Zugriffstoken. Liefert im Mock-Modus null. */
  getAccessToken: (
    scopes: string[],
    options?: AccessTokenOptions,
  ) => Promise<string | null>
  /** Wird bei 401 und 403 aufgerufen, damit die Shell reagieren kann. */
  onAuthError?: (error: ApiError) => void
}

/** Eine Adresse, die schon vollständig ist — Schema und Host inbegriffen. */
function istAbsolut(path: string): boolean {
  return /^https?:\/\//i.test(path)
}

function buildUrl(
  baseUrl: string,
  path: string,
  query: ApiRequestOptions['query'],
): string {
  // Eine absolute Adresse bleibt, wie sie ist. Die Upload- und
  // Download-Adressen der Dateiablage zeigen auf die Plattform und nicht auf
  // den Modulpfad; sie an die Modul-Basis zu hängen ergäbe eine Adresse, die
  // es nicht gibt.
  const url = istAbsolut(path)
    ? path
    : `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    params.append(key, String(value))
  }
  const search = params.toString()
  if (!search) return url

  // Mit `&` anhängen, wenn schon ein Abfrageteil dasteht. Die Adressen der
  // Dateiablage tragen ihr `?token=` bereits mit sich — ein zweites `?` machte
  // daraus eine ungültige Adresse, und der Fehler sähe aus wie ein abgelaufenes
  // Token.
  return url.includes('?') ? `${url}&${search}` : `${url}?${search}`
}

function newTraceId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

async function readProblem(response: Response): Promise<ProblemDetails | undefined> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) return undefined
  try {
    return (await response.json()) as ProblemDetails
  } catch {
    return undefined
  }
}

const STATUS_TITLES: Record<number, string> = {
  400: 'Die Eingabe wurde nicht akzeptiert',
  401: 'Nicht angemeldet',
  403: 'Keine Berechtigung',
  404: 'Nicht gefunden',
  409: 'Der Eintrag wurde zwischenzeitlich geändert',
  422: 'Die Eingabe ist unvollständig',
  500: 'Fehler im Server',
  503: 'Der Dienst ist nicht erreichbar',
}

/**
 * Baut einen fetch-Wrapper für genau ein Modul. Wird von der Shell aufgerufen —
 * Module holen sich den fertigen Client über {@link useApiClient}.
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  async function requestResponse(
    method: string,
    path: string,
    body: unknown,
    requestOptions: ApiRequestOptions = {},
    accept = 'application/json',
  ): Promise<Response> {
    const url = buildUrl(options.baseUrl, path, requestOptions.query)

    // Ein Blob wird unverändert gesendet: kein JSON.stringify, kein
    // application/json. Anders käme beim Speicher eine in Anführungszeichen
    // gefasste Zeichenkette an statt der Datei.
    //
    // Ein Blob ist mehrfach lesbar. Die Wiederholung nach einem 401 ist deshalb
    // auch beim Hochladen unbedenklich — bei einem Strom wäre sie es nicht.
    const roh = body instanceof Blob ? body : undefined

    async function send(forceRefresh = false): Promise<{
      response: Response
      traceId: string
    }> {
      const traceId = newTraceId()
      const headers: Record<string, string> = {
        Accept: accept,
        'X-Trace-Id': traceId,
        ...requestOptions.headers,
      }

      const token = forceRefresh
        ? await options.getAccessToken(options.scopes, { forceRefresh: true })
        : await options.getAccessToken(options.scopes)
      if (token) headers.Authorization = `Bearer ${token}`
      if (roh) {
        // Aus dem Blob und nicht fest verdrahtet. Ist er typlos, bleibt der
        // Kopf weg — dann gilt der Typ, den das Modul beim Anmelden genannt
        // hat, und das ist besser als ein geratener.
        if (roh.type) headers['Content-Type'] = roh.type
      } else if (body !== undefined) {
        headers['Content-Type'] = 'application/json'
      }

      try {
        const response = await fetch(url, {
          method,
          headers,
          ...(roh ? { body: roh } : {}),
          ...(body !== undefined && !roh ? { body: JSON.stringify(body) } : {}),
          ...(requestOptions.signal ? { signal: requestOptions.signal } : {}),
        })
        return { response, traceId }
      } catch (cause) {
        // Abbruch durch TanStack Query ist kein Fehler, den wir übersetzen.
        if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
        throw new ApiError({
          status: 0,
          title: 'Der Server ist nicht erreichbar',
          detail: cause instanceof Error ? cause.message : undefined,
          traceId,
        })
      }
    }

    let { response, traceId } = await send()

    // Ein im MSAL-Cache liegendes Token kann vom Host bereits abgelehnt werden,
    // etwa nach Ablauf oder einer serverseitigen Änderung. Ein 401 ist deshalb
    // noch kein Beweis für eine abgelaufene Anmeldung: einmal beim Aussteller
    // erneuern und denselben Aufruf wiederholen. Auth-Middleware beantwortet
    // das 401, bevor ein schreibender Endpunkt ausgeführt wird; der Retry ist
    // daher auch für POST/PUT/PATCH/DELETE sicher.
    if (response.status === 401 && options.scopes.length > 0) {
      const retry = await send(true)
      response = retry.response
      traceId = retry.traceId
    }

    if (!response.ok) {
      const problem = await readProblem(response)
      const error = new ApiError({
        status: response.status,
        title:
          problem?.title ?? STATUS_TITLES[response.status] ?? `Fehler ${response.status}`,
        detail: problem?.detail,
        traceId: (problem?.traceId as string | undefined) ?? traceId,
        problem,
      })
      if (error.isUnauthorized || error.isForbidden) {
        options.onAuthError?.(error)
      }
      throw error
    }

    return response
  }

  async function request<T>(
    method: string,
    path: string,
    body: unknown,
    requestOptions: ApiRequestOptions = {},
  ): Promise<T> {
    const response = await requestResponse(method, path, body, requestOptions)

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T
    }

    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('json')) {
      return (await response.text()) as T
    }
    return (await response.json()) as T
  }

  async function download(
    path: string,
    requestOptions: ApiRequestOptions = {},
  ): Promise<Blob> {
    const response = await requestResponse(
      'GET',
      path,
      undefined,
      requestOptions,
      'application/octet-stream',
    )
    return await response.blob()
  }

  return {
    get: (path, requestOptions) => request('GET', path, undefined, requestOptions),
    download,
    // PUT und nicht POST: Der Endpunkt der Plattform legt die Datei unter eine
    // Adresse, die schon feststeht — die Anmeldung hat sie vergeben.
    upload: (url, file, requestOptions) => request('PUT', url, file, requestOptions),
    // Dieselbe Mechanik wie download, nur mit einer absoluten Adresse. Der
    // eigene Name steht hier, weil der Unterschied am Aufrufort sichtbar sein
    // soll: Diese Adresse gehört der Plattform und nicht dem Modul.
    downloadFrom: download,
    post: (path, body, requestOptions) => request('POST', path, body, requestOptions),
    put: (path, body, requestOptions) => request('PUT', path, body, requestOptions),
    patch: (path, body, requestOptions) => request('PATCH', path, body, requestOptions),
    delete: (path, requestOptions) => request('DELETE', path, undefined, requestOptions),
  }
}
